import { useState } from 'react';
import { Wallet, Plus, Filter } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@medikei/ui';
import { useToast } from '@/providers/ToastProvider';
import { PayoutBatchList } from '../components/PayoutBatchList';
import {
  useAdminPayoutBatches,
  useGeneratePayoutBatch,
  useProcessPayoutBatch,
} from '../hooks/useAdminPayouts';

export default function AdminPayoutsPage() {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  const { data, isLoading } = useAdminPayoutBatches({
    page,
    limit: 10,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
  });

  const generateMutation = useGeneratePayoutBatch();
  const processMutation = useProcessPayoutBatch();

  const handleGenerate = async () => {
    try {
      const batch = await generateMutation.mutateAsync(undefined);
      toast({ title: `Lote #${batch.id.slice(0, 8)} gerado com sucesso!`, variant: 'success' });
    } catch {
      toast({ title: 'Erro ao gerar lote de pagamento', variant: 'error' });
    }
  };

  const handleProcess = async (batchId: string) => {
    try {
      await processMutation.mutateAsync(batchId);
      toast({ title: 'Lote processado com sucesso!', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao processar lote', variant: 'error' });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wallet className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Lotes de Pagamento</h1>
        </div>
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={generateMutation.isPending}
          isLoading={generateMutation.isPending}
        >
          <Plus className="h-4 w-4 mr-2" />
          Gerar Novo Lote
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos Status</SelectItem>
                <SelectItem value="PENDING">Pendente</SelectItem>
                <SelectItem value="PROCESSING">Processando</SelectItem>
                <SelectItem value="COMPLETED">Concluído</SelectItem>
                <SelectItem value="FAILED">Falhou</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <PayoutBatchList
        batches={data?.data || (data as any)?.items || []}
        isLoading={isLoading}
        total={data?.meta?.total ?? (data as any)?.total ?? 0}
        page={page}
        pageSize={data?.meta?.perPage ?? (data as any)?.limit ?? 10}
        onPageChange={setPage}
        onProcess={handleProcess}
        isProcessing={processMutation.isPending}
      />
    </div>
  );
}
